export interface DeviceInfo {
  type: 'mobile' | 'tablet' | 'desktop';
  screenWidth: number;
  screenHeight: number;
  pixelRatio: number;
  connection: 'slow-2g' | '2g' | '3g' | '4g' | '5g' | 'wifi' | 'ethernet' | 'unknown';
  memory?: number; // GB
  cores?: number;
}

export interface CompressionProfile {
  quality: number;
  maxWidth: number;
  maxHeight: number;
  format: 'jpeg' | 'webp' | 'avif';
  progressive: boolean;
  optimization: 'speed' | 'balanced' | 'quality';
}

export interface AdaptiveCompressionResult {
  blob: Blob;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  profile: CompressionProfile;
  deviceInfo: DeviceInfo;
  networkCondition: string;
}

export class AdaptiveCompressionService {
  private static readonly COMPRESSION_PROFILES = {
    // Perfis por tipo de dispositivo
    mobile: {
      'slow-2g': { quality: 0.6, maxWidth: 800, maxHeight: 600, format: 'jpeg', progressive: false, optimization: 'speed' },
      '2g': { quality: 0.7, maxWidth: 1024, maxHeight: 768, format: 'jpeg', progressive: false, optimization: 'speed' },
      '3g': { quality: 0.75, maxWidth: 1280, maxHeight: 720, format: 'webp', progressive: true, optimization: 'balanced' },
      '4g': { quality: 0.8, maxWidth: 1600, maxHeight: 900, format: 'webp', progressive: true, optimization: 'balanced' },
      '5g': { quality: 0.85, maxWidth: 1920, maxHeight: 1080, format: 'webp', progressive: true, optimization: 'quality' },
      'wifi': { quality: 0.9, maxWidth: 2048, maxHeight: 1152, format: 'webp', progressive: true, optimization: 'quality' },
      'ethernet': { quality: 0.95, maxWidth: 2560, maxHeight: 1440, format: 'avif', progressive: true, optimization: 'quality' }
    },
    tablet: {
      'slow-2g': { quality: 0.65, maxWidth: 1024, maxHeight: 768, format: 'jpeg', progressive: false, optimization: 'speed' },
      '2g': { quality: 0.75, maxWidth: 1280, maxHeight: 720, format: 'jpeg', progressive: true, optimization: 'speed' },
      '3g': { quality: 0.8, maxWidth: 1600, maxHeight: 900, format: 'webp', progressive: true, optimization: 'balanced' },
      '4g': { quality: 0.85, maxWidth: 1920, maxHeight: 1080, format: 'webp', progressive: true, optimization: 'balanced' },
      '5g': { quality: 0.9, maxWidth: 2560, maxHeight: 1440, format: 'webp', progressive: true, optimization: 'quality' },
      'wifi': { quality: 0.92, maxWidth: 2880, maxHeight: 1620, format: 'avif', progressive: true, optimization: 'quality' },
      'ethernet': { quality: 0.95, maxWidth: 3200, maxHeight: 1800, format: 'avif', progressive: true, optimization: 'quality' }
    },
    desktop: {
      'slow-2g': { quality: 0.7, maxWidth: 1280, maxHeight: 720, format: 'jpeg', progressive: true, optimization: 'speed' },
      '2g': { quality: 0.8, maxWidth: 1600, maxHeight: 900, format: 'webp', progressive: true, optimization: 'balanced' },
      '3g': { quality: 0.85, maxWidth: 1920, maxHeight: 1080, format: 'webp', progressive: true, optimization: 'balanced' },
      '4g': { quality: 0.9, maxWidth: 2560, maxHeight: 1440, format: 'webp', progressive: true, optimization: 'quality' },
      '5g': { quality: 0.92, maxWidth: 3200, maxHeight: 1800, format: 'avif', progressive: true, optimization: 'quality' },
      'wifi': { quality: 0.95, maxWidth: 3840, maxHeight: 2160, format: 'avif', progressive: true, optimization: 'quality' },
      'ethernet': { quality: 0.98, maxWidth: 4096, maxHeight: 2304, format: 'avif', progressive: true, optimization: 'quality' }
    }
  };

  /**
   * Detecta informações do dispositivo
   */
  static detectDeviceInfo(): DeviceInfo {
    const userAgent = navigator.userAgent;
    const screen = window.screen;
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    
    // Detectar tipo de dispositivo
    let deviceType: 'mobile' | 'tablet' | 'desktop' = 'desktop';
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
      deviceType = screen.width < 768 ? 'mobile' : 'tablet';
    }

    // Detectar conexão de rede
    let networkType: DeviceInfo['connection'] = 'unknown';
    if (connection) {
      if (connection.effectiveType) {
        networkType = connection.effectiveType as any;
      } else if (connection.type) {
        networkType = connection.type as any;
      }
    }

    // Detectar memória disponível
    let memory: number | undefined;
    if ((navigator as any).deviceMemory) {
      memory = (navigator as any).deviceMemory;
    }

    // Detectar número de cores
    let cores: number | undefined;
    if ((navigator as any).hardwareConcurrency) {
      cores = (navigator as any).hardwareConcurrency;
    }

    return {
      type: deviceType,
      screenWidth: screen.width,
      screenHeight: screen.height,
      pixelRatio: window.devicePixelRatio || 1,
      connection: networkType,
      memory,
      cores
    };
  }

  /**
   * Seleciona perfil de compressão baseado no dispositivo
   */
  static selectCompressionProfile(deviceInfo: DeviceInfo): CompressionProfile {
    const deviceProfiles = this.COMPRESSION_PROFILES[deviceInfo.type];
    const profile = deviceProfiles[deviceInfo.connection] || deviceProfiles['wifi'];

    // Ajustar baseado na resolução da tela
    const adjustedProfile = { ...profile };
    
    if (deviceInfo.screenWidth < profile.maxWidth) {
      adjustedProfile.maxWidth = deviceInfo.screenWidth;
    }
    
    if (deviceInfo.screenHeight < profile.maxHeight) {
      adjustedProfile.maxHeight = deviceInfo.screenHeight;
    }

    // Ajustar baseado na memória disponível
    if (deviceInfo.memory && deviceInfo.memory < 2) {
      adjustedProfile.quality = Math.max(0.6, adjustedProfile.quality - 0.1);
      adjustedProfile.optimization = 'speed';
    }

    // Ajustar baseado no número de cores
    if (deviceInfo.cores && deviceInfo.cores < 4) {
      adjustedProfile.optimization = 'speed';
    }

    return adjustedProfile;
  }

  /**
   * Comprime imagem de forma adaptativa
   */
  static async compressAdaptively(
    file: File,
    customProfile?: Partial<CompressionProfile>
  ): Promise<AdaptiveCompressionResult> {
    const deviceInfo = this.detectDeviceInfo();
    const baseProfile = this.selectCompressionProfile(deviceInfo);
    const profile = customProfile ? { ...baseProfile, ...customProfile } : baseProfile;

    console.log('📱 Compressão adaptativa:', { deviceInfo, profile });

    try {
      // Criar canvas para processamento
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        throw new Error('Canvas 2D context não disponível');
      }

      // Carregar imagem
      const img = await this.loadImage(file);
      
      // Calcular dimensões finais
      const { width, height } = this.calculateDimensions(
        img.width,
        img.height,
        profile.maxWidth,
        profile.maxHeight
      );

      // Configurar canvas
      canvas.width = width;
      canvas.height = height;

      // Aplicar configurações de qualidade
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = profile.optimization === 'quality' ? 'high' : 'medium';

      // Desenhar imagem redimensionada
      ctx.drawImage(img, 0, 0, width, height);

      // Aplicar filtros baseados na otimização
      if (profile.optimization === 'speed') {
        // Reduzir qualidade para velocidade
        profile.quality = Math.max(0.5, profile.quality - 0.1);
      }

      // Converter para blob com formato e qualidade especificados
      const blob = await this.canvasToBlob(canvas, profile.format, profile.quality);

      // Calcular estatísticas
      const originalSize = file.size;
      const compressedSize = blob.size;
      const compressionRatio = ((originalSize - compressedSize) / originalSize) * 100;

      return {
        blob,
        originalSize,
        compressedSize,
        compressionRatio,
        profile,
        deviceInfo,
        networkCondition: deviceInfo.connection
      };

    } catch (error) {
      throw new Error(`Erro na compressão adaptativa: ${error.message}`);
    }
  }

  /**
   * Comprime múltiplas imagens com perfis adaptativos
   */
  static async compressBatchAdaptively(
    files: File[],
    customProfile?: Partial<CompressionProfile>
  ): Promise<AdaptiveCompressionResult[]> {
    const results: AdaptiveCompressionResult[] = [];
    
    for (const file of files) {
      try {
        const result = await this.compressAdaptively(file, customProfile);
        results.push(result);
      } catch (error) {
        console.error(`Erro ao comprimir ${file.name}:`, error);
        // Adicionar resultado de erro
        results.push({
          blob: file,
          originalSize: file.size,
          compressedSize: file.size,
          compressionRatio: 0,
          profile: this.selectCompressionProfile(this.detectDeviceInfo()),
          deviceInfo: this.detectDeviceInfo(),
          networkCondition: 'error'
        });
      }
    }
    
    return results;
  }

  /**
   * Otimiza imagem para diferentes dispositivos
   */
  static async optimizeForMultipleDevices(
    file: File
  ): Promise<{
    mobile: Blob;
    tablet: Blob;
    desktop: Blob;
    stats: AdaptiveCompressionResult[];
  }> {
    const devices: Array<'mobile' | 'tablet' | 'desktop'> = ['mobile', 'tablet', 'desktop'];
    const results: AdaptiveCompressionResult[] = [];
    const blobs: { [key: string]: Blob } = {};

    for (const deviceType of devices) {
      const mockDeviceInfo: DeviceInfo = {
        type: deviceType,
        screenWidth: deviceType === 'mobile' ? 375 : deviceType === 'tablet' ? 768 : 1920,
        screenHeight: deviceType === 'mobile' ? 667 : deviceType === 'tablet' ? 1024 : 1080,
        pixelRatio: 2,
        connection: 'wifi',
        memory: deviceType === 'mobile' ? 2 : deviceType === 'tablet' ? 4 : 8,
        cores: deviceType === 'mobile' ? 4 : deviceType === 'tablet' ? 6 : 8
      };

      const profile = this.selectCompressionProfile(mockDeviceInfo);
      const result = await this.compressAdaptively(file, profile);
      
      results.push(result);
      blobs[deviceType] = await this.compressImage(file, profile);
    }

    return {
      mobile: blobs.mobile,
      tablet: blobs.tablet,
      desktop: blobs.desktop,
      stats: results
    };
  }

  /**
   * Carrega imagem do arquivo
   */
  private static loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Falha ao carregar imagem'));
      };

      img.src = url;
    });
  }

  /**
   * Calcula dimensões mantendo proporção
   */
  private static calculateDimensions(
    originalWidth: number,
    originalHeight: number,
    maxWidth: number,
    maxHeight: number
  ): { width: number; height: number } {
    const aspectRatio = originalWidth / originalHeight;
    
    if (originalWidth > maxWidth || originalHeight > maxHeight) {
      if (maxWidth / aspectRatio <= maxHeight) {
        return {
          width: maxWidth,
          height: Math.round(maxWidth / aspectRatio)
        };
      } else {
        return {
          width: Math.round(maxHeight * aspectRatio),
          height: maxHeight
        };
      }
    }

    return { width: originalWidth, height: originalHeight };
  }

  /**
   * Converte canvas para blob
   */
  private static async canvasToBlob(
    canvas: HTMLCanvasElement,
    format: string,
    quality: number
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      try {
        let mimeType: string;
        
        switch (format) {
          case 'webp':
            mimeType = 'image/webp';
            break;
          case 'avif':
            mimeType = 'image/avif';
            break;
          default:
            mimeType = 'image/jpeg';
        }

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Falha ao criar blob'));
            }
          },
          mimeType,
          quality
        );
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Comprime imagem com perfil específico
   */
  private static async compressImage(
    file: File,
    profile: CompressionProfile
  ): Promise<Blob> {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      throw new Error('Canvas 2D context não disponível');
    }

    const img = await this.loadImage(file);
    const { width, height } = this.calculateDimensions(
      img.width,
      img.height,
      profile.maxWidth,
      profile.maxHeight
    );

    canvas.width = width;
    canvas.height = height;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = profile.optimization === 'quality' ? 'high' : 'medium';
    ctx.drawImage(img, 0, 0, width, height);

    return this.canvasToBlob(canvas, profile.format, profile.quality);
  }

  /**
   * Obtém estatísticas de compressão
   */
  static getCompressionStats(results: AdaptiveCompressionResult[]): {
    totalFiles: number;
    totalOriginalSize: number;
    totalCompressedSize: number;
    averageCompressionRatio: number;
    deviceBreakdown: { [key: string]: number };
    networkBreakdown: { [key: string]: number };
  } {
    const totalFiles = results.length;
    const totalOriginalSize = results.reduce((sum, r) => sum + r.originalSize, 0);
    const totalCompressedSize = results.reduce((sum, r) => sum + r.compressedSize, 0);
    const spaceSaved = totalOriginalSize - totalCompressedSize;
    const averageCompressionRatio = totalFiles > 0 ? 
      results.reduce((sum, r) => sum + r.compressionRatio, 0) / totalFiles : 0;

    // Breakdown por dispositivo
    const deviceBreakdown = results.reduce((acc, r) => {
      acc[r.deviceInfo.type] = (acc[r.deviceInfo.type] || 0) + 1;
      return acc;
    }, {} as { [key: string]: number });

    // Breakdown por rede
    const networkBreakdown = results.reduce((acc, r) => {
      acc[r.networkCondition] = (acc[r.networkCondition] || 0) + 1;
      return acc;
    }, {} as { [key: string]: number });

    return {
      totalFiles,
      totalOriginalSize,
      totalCompressedSize,
      averageCompressionRatio,
      deviceBreakdown,
      networkBreakdown
    };
  }
}
